import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import PerfectScrollbar from "react-perfect-scrollbar";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Avatar,
  Typography,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import LinkOutlinedIcon from "@material-ui/icons/Link";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { toast } from "react-toastify";
import { getExperienceById } from "src/actions/ExperienceActions";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { connect } from "react-redux";
import api from "src/utils/api";

// ─── Design tokens ─────────────────────────────────────────────────────────
const TOKENS = {
  surface: "#FFFFFF",
  border: "#EEF0F5",
  borderStrong: "#E5E7EB",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",
  accent: "#222222",
  divider: "#F1F5F9",
};

const useStyles = makeStyles((theme) => ({
  card: {
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 16,
    boxShadow: "none",
    overflow: "hidden",
  },
  tableHead: {
    backgroundColor: "#FAFBFC",
  },
  tableHeadCell: {
    color: TOKENS.textSecondary,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    borderBottom: `1px solid ${TOKENS.border}`,
    padding: "14px 20px",
  },
  tableRow: {
    transition: "background 0.15s ease",
    "&:hover": { backgroundColor: "#FAFBFC" },
  },
  tableCell: {
    padding: "16px 20px",
    borderBottom: `1px solid ${TOKENS.divider}`,
    fontSize: 14,
    color: TOKENS.textPrimary,
    verticalAlign: "middle",
  },
  titleCell: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  thumb: {
    width: 44,
    height: 44,
    borderRadius: 10,
    flexShrink: 0,
    backgroundColor: TOKENS.divider,
  },
  thumbFallback: {
    width: 44,
    height: 44,
    borderRadius: 10,
    flexShrink: 0,
    backgroundColor: "#F2F2F2",
    color: TOKENS.accent,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 700,
  },
  titleText: {
    fontSize: 14,
    fontWeight: 600,
    color: TOKENS.textPrimary,
    lineHeight: 1.3,
  },
  titleSub: {
    fontSize: 12,
    color: TOKENS.textMuted,
    marginTop: 2,
  },
  priceText: {
    fontSize: 14,
    fontWeight: 600,
    color: TOKENS.textPrimary,
  },
  priceUnit: {
    fontSize: 12,
    color: TOKENS.textMuted,
    fontWeight: 400,
    marginLeft: 4,
  },
  // Status pill
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
  },
  statusApproved: { backgroundColor: "#ECFDF5", color: "#059669" },
  statusPending: { backgroundColor: "#FEF3C7", color: "#B45309" },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  // Actions
  actions: {
    display: "flex",
    gap: 4,
    justifyContent: "flex-end",
  },
  iconBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    color: TOKENS.textSecondary,
    "&:hover": {
      backgroundColor: TOKENS.divider,
      color: TOKENS.textPrimary,
    },
  },
  iconBtnDanger: {
    width: 32,
    height: 32,
    borderRadius: 8,
    color: TOKENS.textSecondary,
    "&:hover": {
      backgroundColor: "#FEF2F2",
      color: "#EF4444",
    },
  },
  // Empty state
  emptyState: {
    padding: "64px 20px",
    textAlign: "center",
  },
  emptyIcon: {
    width: 56,
    height: 56,
    borderRadius: 16,
    backgroundColor: "#F2F2F2",
    color: TOKENS.accent,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
    fontSize: 24,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: TOKENS.textPrimary,
    marginBottom: 4,
  },
  emptySub: {
    fontSize: 13,
    color: TOKENS.textSecondary,
  },
  pagination: {
    borderTop: `1px solid ${TOKENS.border}`,
    "& .MuiTablePagination-toolbar": {
      paddingLeft: 16,
      paddingRight: 8,
    },
    "& .MuiTablePagination-caption": {
      fontSize: 13,
      color: TOKENS.textSecondary,
    },
  },
}));

const ExperienceListResults = ({
  experiences = [],
  getExperienceById,
  ...rest
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);

  const onDelete = async (expId) => {
    try {
      await api.delete(`experiences/host/experience/${expId}`);
      window.location.reload();
    } catch (error) {
      toast.error("Action failed!");
    }
  };

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePageChange = (_event, newPage) => setPage(newPage);

  const viewExperience = async (experienceId) => {
    await getExperienceById(experienceId, navigate);
  };

  const paged = experiences.slice(page * limit, page * limit + limit);

  return (
    <Card className={classes.card} {...rest}>
      {experiences.length === 0 ? (
        <Box className={classes.emptyState}>
          <Box className={classes.emptyIcon}>—</Box>
          <Typography className={classes.emptyTitle}>
            No experiences yet
          </Typography>
          <Typography className={classes.emptySub}>
            When you create an experience, it'll show up here.
          </Typography>
        </Box>
      ) : (
        <>
          <PerfectScrollbar>
            <Box style={{ minWidth: 900 }}>
              <Table>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell className={classes.tableHeadCell}>
                      Experience
                    </TableCell>
                    <TableCell className={classes.tableHeadCell}>
                      Price
                    </TableCell>
                    <TableCell className={classes.tableHeadCell}>
                      Country
                    </TableCell>
                    <TableCell className={classes.tableHeadCell}>
                      Status
                    </TableCell>
                    <TableCell className={classes.tableHeadCell} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((experience) => (
                    <TableRow
                      hover
                      key={experience.id}
                      className={classes.tableRow}
                    >
                      {/* Title + thumbnail */}
                      <TableCell className={classes.tableCell}>
                        <Box className={classes.titleCell}>
                          {experience.coverImage_URL ? (
                            <Avatar
                              src={experience.coverImage_URL}
                              variant="rounded"
                              className={classes.thumb}
                            />
                          ) : (
                            <Box className={classes.thumbFallback}>
                              {experience.title?.[0]?.toUpperCase() || "E"}
                            </Box>
                          )}
                          <Box>
                            <Typography className={classes.titleText}>
                              {experience.title || "Untitled"}
                            </Typography>
                            {experience.district && (
                              <Typography className={classes.titleSub}>
                                {experience.district}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Price */}
                      <TableCell className={classes.tableCell}>
                        <span className={classes.priceText}>
                          {experience.price}
                        </span>
                        <span className={classes.priceUnit}>USD</span>
                      </TableCell>

                      {/* Country */}
                      <TableCell className={classes.tableCell}>
                        {experience.country || "—"}
                      </TableCell>

                      {/* Status */}
                      <TableCell className={classes.tableCell}>
                        <span
                          className={`${classes.statusPill} ${
                            experience.approved
                              ? classes.statusApproved
                              : classes.statusPending
                          }`}
                        >
                          <span
                            className={classes.statusDot}
                            style={{
                              backgroundColor: experience.approved
                                ? "#10B981"
                                : "#F59E0B",
                            }}
                          />
                          {experience.approved ? "Approved" : "Pending"}
                        </span>
                      </TableCell>

                      {/* Actions */}
                      <TableCell className={classes.tableCell} align="right">
                        <Box className={classes.actions}>
                          <Tooltip title="Edit">
                            <IconButton
                              className={classes.iconBtn}
                              onClick={() => viewExperience(experience.id)}
                              size="small"
                            >
                              <EditOutlinedIcon style={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>

                          {experience.approved && (
                            <CopyToClipboard
                              text={`https://tubayo.com/experience/${experience.id}`}
                              onCopy={() => toast.success("Link copied!")}
                            >
                              <Tooltip title="Copy public link">
                                <IconButton
                                  className={classes.iconBtn}
                                  size="small"
                                >
                                  <LinkOutlinedIcon style={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </CopyToClipboard>
                          )}

                          <Tooltip title="Delete">
                            <IconButton
                              className={classes.iconBtnDanger}
                              size="small"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    "Are you sure you wish to delete this experience?",
                                  )
                                )
                                  onDelete(experience.id);
                              }}
                            >
                              <DeleteOutlineIcon style={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </PerfectScrollbar>

          <TablePagination
            component="div"
            className={classes.pagination}
            count={experiences.length}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </>
      )}
    </Card>
  );
};

ExperienceListResults.propTypes = {
  experiences: PropTypes.array.isRequired,
};

export default connect(null, { getExperienceById })(ExperienceListResults);
